import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { setAuth0Client } from "@/services/token-service";

const AuthTokenContext = createContext(null);

export function AuthTokenProvider({ children }) {
  const [accessToken, setAccessToken] = useState(null);
  const [isAuthReady, setIsAuthReady] = useState(false);
  const { user, isLoading, isAuthenticated, getAccessTokenSilently } = useAuth0();

  // Register the Auth0 client with token-service when user is authenticated
  useEffect(() => {
    if (isAuthenticated && user) {
      setAuth0Client(getAccessTokenSilently, user);
      setIsAuthReady(true);
    } else if (!isLoading && !isAuthenticated) {
      // Not authenticated and not loading - auth is "ready" (just not logged in)
      setIsAuthReady(false);
    }
  }, [isAuthenticated, user, isLoading, getAccessTokenSilently]);

  // Function to get a fresh access token
  const getAccessToken = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      setAccessToken(token);
      return token;
    } catch (error) {
      console.error("Error getting access token:", error);
      throw error;
    }
  }, [getAccessTokenSilently]);

  // Function to refresh the token
  const refreshToken = useCallback(async () => {
    return await getAccessToken();
  }, [getAccessToken]);

  // Get initial token on load if user is authenticated
  useEffect(() => {
    if (user && !accessToken && !isLoading) {
      getAccessToken().catch(console.error);
    }
  }, [user, accessToken, isLoading, getAccessToken]);

  return (
    <AuthTokenContext.Provider value={{ accessToken, refreshToken, isAuthReady }}>
      {children}
    </AuthTokenContext.Provider>
  );
}

export function useAuthToken() {
  const context = useContext(AuthTokenContext);
  if (!context) {
    throw new Error("useAuthToken must be used within an AuthTokenProvider");
  }
  return context;
}
